'use client';

import dayjs, { type Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';

import { DatePicker, MainButton, Modal, SecondaryButton } from '@web/ui';

type CompleteJobModalProps = {
  open: boolean;
  isProcessing?: boolean;
  initialDate?: Dayjs | null;
  onConfirm: (completionDate: Dayjs) => Promise<void> | void;
  onClose: () => void;
};

export function CompleteJobModal({
  open,
  isProcessing = false,
  initialDate = null,
  onConfirm,
  onClose,
}: CompleteJobModalProps) {
  if (!open) {
    return null;
  }

  const resolvedInitialDate = initialDate ?? dayjs().startOf('day');
  const resetKey = resolvedInitialDate.format('YYYY-MM-DD');

  return (
    <CompleteJobModalBody
      key={resetKey}
      isProcessing={isProcessing}
      initialDate={resolvedInitialDate}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

type CompleteJobModalBodyProps = {
  isProcessing: boolean;
  initialDate: Dayjs;
  onConfirm: (completionDate: Dayjs) => Promise<void> | void;
  onClose: () => void;
};

function CompleteJobModalBody({
  isProcessing,
  initialDate,
  onConfirm,
  onClose,
}: CompleteJobModalBodyProps) {
  const [completionDate, setCompletionDate] = useState<Dayjs | null>(
    initialDate,
  );
  const [dateError, setDateError] = useState('');

  const helperText = useMemo(() => {
    if (!completionDate) return '';
    return `Completion date: ${completionDate.format('MM/DD/YYYY')}`;
  }, [completionDate]);

  const handleConfirm = async () => {
    if (!completionDate) {
      setDateError('Completion date is required.');
      return;
    }

    await onConfirm(completionDate);
  };

  return (
    <Modal
      open
      onClose={() => {
        if (isProcessing) return;
        onClose();
      }}
      width="min(560px, 92vw)"
    >
      <div className="card-container-glass">
        <h3 style={{ margin: 0 }}>Mark completed</h3>
        <p
          style={{
            margin: '0.6rem 0 0',
            color: 'var(--nn-color-text-secondary)',
          }}
        >
          Set a completion date before transitioning this job to completed.
        </p>

        <div style={{ marginTop: '1rem' }}>
          <DatePicker
            name="job-completion-date"
            label="Completion Date"
            value={completionDate}
            error={dateError}
            helperText={dateError ? undefined : helperText}
            onChange={(value) => {
              setDateError('');
              setCompletionDate(value);
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginTop: '1.25rem',
          }}
        >
          <SecondaryButton
            ariaLabel="Cancel mark completed"
            onClick={() => {
              if (isProcessing) return;
              onClose();
            }}
          >
            Cancel
          </SecondaryButton>
          <MainButton
            ariaLabel="Confirm mark completed"
            onClick={() => void handleConfirm()}
            isLoading={isProcessing}
            disabled={isProcessing}
          >
            Mark completed
          </MainButton>
        </div>
      </div>
    </Modal>
  );
}
